import { Button as MantineButton, ButtonProps, Tooltip, useMantineTheme } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { hasAccess, type AccessRule } from '../../security/AccessControl';
import { useEnrichedAuth } from '../../../hooks/useEnrichedAuth';
import { getContrastingTextColor } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type HoverEffect = 'glow' | 'lift' | 'scale' | 'none';

export interface ThemedButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'gradient' | 'filled' | 'light' | 'outline' | 'subtle' | 'default' | 'transparent';
  gradientVariant?: GradientVariant;
  hoverEffect?: HoverEffect;
  accessRule?: AccessRule;
  accessMode?: 'hide' | 'disable';
  accessMessage?: string;
  /** Click handler - explicitly declared for better TypeScript inference */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  type?: 'button' | 'submit' | 'reset';
  /** React Router Link component for internal navigation */
  component?: any;
  /** Route path when using component prop (e.g., with Link) */
  to?: string;
  /** Busy/loading state indicator (alias for loading) */
  isBusy?: boolean;
  /** HTML title attribute for tooltips */
  title?: string;
  /** URL for anchor links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const Button = forwardRef<HTMLButtonElement, ThemedButtonProps>(
  (
    {
      variant = 'gradient',
      gradientVariant = 'primary',
      hoverEffect = 'lift',
      accessRule,
      accessMode = 'disable',
      accessMessage = 'You do not have permission to perform this action',
      isBusy,
      children,
      style,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { user } = useEnrichedAuth();
    const lineSpacing = usePreferencesStore((s) => s.lineSpacing);
    const letterSpacingPref = usePreferencesStore((s) => s.letterSpacing);

    // Accessibility styles
    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Use enriched user with permissions field
    const allowed = hasAccess(
      user?.roles ?? [],
      user?.permissions ?? [],
      user?.domains ?? [],
      accessRule
    );

    if (accessRule && !allowed && accessMode === 'hide') {
      return null;
    }

    const baseButton =
      variant !== 'gradient' ? (
        <MantineButton
          ref={ref}
          variant={variant}
          style={{ ...accessibilityStyles, ...style }}
          disabled={accessRule ? !allowed || rest.disabled : rest.disabled}
          loading={isBusy ?? rest.loading}
          aria-disabled={accessRule ? (!allowed || rest.disabled) : rest.disabled}
          {...rest}
        >
          {children}
        </MantineButton>
      ) : (
        (() => {
          const gradient = theme.other?.gradients?.[gradientVariant] || {
            from: theme.colors.blue[6],
            to: theme.colors.cyan[6],
          };

          // Calculate text color for gradient background
          const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
          const autoTextColor = useMemo(
            () => getContrastingTextColor(gradientBg),
            [gradientBg]
          );

          const hoverStyles = {
            glow: {
              '--hover-shadow': `0 0 20px ${gradient.from}60`,
            },
            lift: {
              '--hover-transform': 'translateY(-2px)',
              '--hover-shadow': '0 4px 12px rgba(0, 0, 0, 0.15)',
            },
            scale: {
              '--hover-transform': 'scale(1.02)',
            },
            none: {},
          };

          return (
            <MantineButton
              ref={ref}
              style={{
                backgroundImage: gradientBg,
                color: style?.color || autoTextColor,
                border: 'none',
                transition: 'all 0.2s ease',
                ...accessibilityStyles,
                ...hoverStyles[hoverEffect],
                ...style,
              }}
              disabled={accessRule ? !allowed || rest.disabled : rest.disabled}
              loading={isBusy ?? rest.loading}
              aria-disabled={accessRule ? (!allowed || rest.disabled) : rest.disabled}
              {...rest}
            >
              {children}
            </MantineButton>
          );
        })()
      );

    if (accessRule && !allowed && accessMode === 'disable') {
      return (
        <Tooltip label={accessMessage} disabled={rest.disabled}>
          <span style={{ display: 'inline-flex' }}>{baseButton}</span>
        </Tooltip>
      );
    }

    return baseButton;
  }
);

Button.displayName = 'Button';

export default Button;
